import {
  type CarWheelIdentifiersRecord,
  CarWheelIdentifiers,
  CarWheelIdentifiersDescriptions,
  CarWheelIdentifiersLabels
} from '@navio/nsdk';
import type { NoddiIconName } from 'noddi-ui-common';
import { getLabelMapFromEntries } from 'noddi-util';

export type CarWheelIdentifierDisplay = {
  value: CarWheelIdentifiers;
  label: string;
  description: string;
  icon: NoddiIconName;
};

/** Unique icon per CarWheelIdentifiers member — used in columns and filter sidebar. */
const CAR_WHEEL_IDENTIFIER_ICONS: Record<CarWheelIdentifiers, NoddiIconName> = {
  [CarWheelIdentifiers.IS_STUDDED]: 'MountainSnow',
  [CarWheelIdentifiers.IS_RUN_FLAT]: 'ShieldCheck',
  [CarWheelIdentifiers.IS_C_TYRE]: 'Truck',
  [CarWheelIdentifiers.IS_WINTER_WORN]: 'Thermometer',
  [CarWheelIdentifiers.HAS_BOLTS]: 'Wrench',
  [CarWheelIdentifiers.HAS_HUB_CAPS]: 'Circle',
  [CarWheelIdentifiers.HAS_WHEEL_LOCK]: 'Lock',
  [CarWheelIdentifiers.HAS_NITROGEN]: 'Droplet',
  [CarWheelIdentifiers.HAS_AIR_SUSPENSION]: 'Layers',
  [CarWheelIdentifiers.HAS_TPMS]: 'Target',
  [CarWheelIdentifiers.IS_SNOW_WEAR]: 'Snowflake'
};

const CAR_WHEEL_IDENTIFIER_LABEL_BY_VALUE = getLabelMapFromEntries(CarWheelIdentifiers, CarWheelIdentifiersLabels);

const CAR_WHEEL_IDENTIFIER_DESCRIPTION_BY_VALUE = getLabelMapFromEntries(
  CarWheelIdentifiers,
  CarWheelIdentifiersDescriptions
);

const normalizeIdentifierValues = (
  record: CarWheelIdentifiersRecord | CarWheelIdentifiers[] | undefined | null
): CarWheelIdentifiers[] => {
  if (!record) {
    return [];
  }
  if (Array.isArray(record)) {
    return record;
  }
  return record.identifiers ?? [];
};

export const toCarWheelIdentifierDisplays = (
  record: CarWheelIdentifiersRecord | CarWheelIdentifiers[] | undefined | null
): CarWheelIdentifierDisplay[] => {
  const values = normalizeIdentifierValues(record);
  if (values.length === 0) {
    return [];
  }

  const tpmsTypeLabel = Array.isArray(record) ? undefined : record?.tpmsType?.label;

  const displays: CarWheelIdentifierDisplay[] = [];
  for (const value of values) {
    const icon = CAR_WHEEL_IDENTIFIER_ICONS[value];
    const baseDescription =
      CAR_WHEEL_IDENTIFIER_DESCRIPTION_BY_VALUE[value] ?? CAR_WHEEL_IDENTIFIER_LABEL_BY_VALUE[value] ?? value;
    const description =
      value === CarWheelIdentifiers.HAS_TPMS && tpmsTypeLabel
        ? `${baseDescription} (${tpmsTypeLabel})`
        : baseDescription;
    displays.push({
      value,
      label: CAR_WHEEL_IDENTIFIER_LABEL_BY_VALUE[value] ?? value,
      description,
      icon
    });
  }
  return displays;
};

export const CAR_WHEEL_IDENTIFIER_FILTER_OPTIONS = Object.values(CarWheelIdentifiers).map((value) => ({
  value,
  label: CAR_WHEEL_IDENTIFIER_LABEL_BY_VALUE[value] ?? value,
  icon: CAR_WHEEL_IDENTIFIER_ICONS[value],
  description: CAR_WHEEL_IDENTIFIER_DESCRIPTION_BY_VALUE[value]
}));
